import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material'
import axios from 'axios'
import React, { useState } from 'react'

import { getUser } from 'pared/utils/user'

import { IQueryFilter } from '../../type'

interface DataCorrectionFormsProps {
  startDate?: string
  queryFilter: IQueryFilter | null
  onDataSubmitted?: () => void
}

const DataCorrectionForms: React.FC<DataCorrectionFormsProps> = ({
  startDate = '',
  queryFilter,
  onDataSubmitted,
}) => {
  // Form states
  const [netSalesValue, setNetSalesValue] = useState('')
  const [checkCountValue, setCheckCountValue] = useState('')

  // Loading state
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Error state
  const [error, setError] = useState<string | null>(null)

  // Success state
  const [success, setSuccess] = useState(false)

  // Get location ID from queryFilter
  const locationId = queryFilter?.locationIds?.[0]

  // Format number with commas
  const formatNumberWithCommas = (value: string) => {
    // Remove all non-digit characters except decimal point
    const cleanedValue = value.replace(/[^\d.]/g, '')

    // Split into integer and decimal parts
    const parts = cleanedValue.split('.')

    // Format integer part with commas
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')

    // Join back together
    return parts.join('.')
  }

  // Handle net sales input change
  const handleNetSalesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const numericValue = value.replace(/[^\d.]/g, '')

    // Only allow one decimal point
    const decimalCount = (numericValue.match(/\./g) || []).length
    if (decimalCount > 1) return

    setNetSalesValue(numericValue)
    setError(null)
    setSuccess(false)
  }

  // Handle check count input change
  const handleCheckCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    const numericValue = value.replace(/[^\d]/g, '') // Only allow digits, no decimals

    setCheckCountValue(numericValue)
    setError(null)
    setSuccess(false)
  }

  // Submit form
  const handleSubmit = async () => {
    if (!netSalesValue || !locationId || !startDate) {
      setError('Net sales is required')
      return
    }

    setIsSubmitting(true)
    setError(null)
    setSuccess(false)

    try {
      const user = getUser()

      const requestBody: any = {
        userId: user.userId,
        userJwt: user.jwt,
        targetDate: startDate,
        locationId: locationId,
        correctedNetSales: parseFloat(netSalesValue),
        requestType: 'CORRECT_NET_SALES_AND_CHECK_COUNT',
      }

      // Add check count only if provided
      if (checkCountValue) {
        requestBody.correctedCheckCount = parseInt(checkCountValue, 10)
      }

      await axios.post(
        `${process.env.REACT_APP_BE_BASE_URL}/correct_data_manually`,
        requestBody,
        {
          timeout: 60000,
        },
      )

      setSuccess(true)
      setNetSalesValue('')
      setCheckCountValue('')

      // Trigger refresh of history table
      if (onDataSubmitted) {
        onDataSubmitted()
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.error || 'Failed to submit data correction'
      setError(errorMessage)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Box>
      {/* Combined Form */}
      <Box>
        {/* Net Sales Input */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontFamily: 'Lexend-SemiBold',
              fontSize: '0.875rem',
              mb: 1,
            }}
          >
            Correct Net Sales
            <Typography
              component="span"
              sx={{
                color: 'error.main',
                ml: 0.5,
                fontFamily: 'Lexend-SemiBold',
              }}
            >
              *
            </Typography>
          </Typography>
          <TextField
            value={
              netSalesValue ? `$${formatNumberWithCommas(netSalesValue)}` : ''
            }
            onChange={handleNetSalesChange}
            placeholder="$0.00"
            size="small"
            required
            error={!!error && !netSalesValue}
            sx={{
              width: '200px',
              '& .MuiInputBase-input': {
                fontFamily: 'Lexend-Regular',
                fontSize: '0.875rem',
              },
            }}
            disabled={isSubmitting}
          />
        </Box>

        {/* Check Count Input */}
        <Box sx={{ mb: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontFamily: 'Lexend-SemiBold',
              fontSize: '0.875rem',
              mb: 1,
            }}
          >
            Correct Check Count
          </Typography>
          <TextField
            value={
              checkCountValue ? formatNumberWithCommas(checkCountValue) : ''
            }
            onChange={handleCheckCountChange}
            placeholder="0"
            size="small"
            sx={{
              width: '200px',
              '& .MuiInputBase-input': {
                fontFamily: 'Lexend-Regular',
                fontSize: '0.875rem',
              },
            }}
            disabled={isSubmitting}
          />
        </Box>

        {/* Submit Button */}
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || !netSalesValue}
          size="small"
          sx={{
            fontFamily: 'Lexend-Regular',
            fontSize: '0.875rem',
            textTransform: 'none',
            minWidth: '80px',
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            'Submit'
          )}
        </Button>

        {/* Error Message */}
        {error && (
          <Alert
            severity="error"
            sx={{
              mt: 2,
              '& .MuiAlert-message': {
                fontFamily: 'Lexend-Regular',
                fontSize: '0.875rem',
              },
            }}
          >
            {error}
          </Alert>
        )}

        {/* Success Message */}
        {success && (
          <Alert
            severity="success"
            sx={{
              mt: 2,
              '& .MuiAlert-message': {
                fontFamily: 'Lexend-Regular',
                fontSize: '0.875rem',
              },
            }}
          >
            Data correction submitted successfully
          </Alert>
        )}
      </Box>
    </Box>
  )
}

export default DataCorrectionForms
